import React, { useState, useEffect } from 'react';
// [Web UX 3. csomag (D), 2026-10-04] A kérés-hibák szövege a közös `apiErrorMessage`-en át megy (a szerver magyar
// indoklása mindhárom törzs-alakból / hálózati hibánál magyar mondat / különben „<tartalék> (HTTP n)"),
// a nyers `err.response?.data?.message` helyett. Őr: `utils/__tests__/rawErrorMessages.guard.test.ts`.
import { apiErrorMessage } from '../../utils/apiErrors';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import DayColumn from './DayColumn';
import FilterBar from './FilterBar';
import PlannedEventDialog from './PlannedEventDialog';
import plannedEventService, {
  getOrderedDaysOfWeek,
  translateDayOfWeek,
} from '../../services/plannedEventService';
import { venueService } from '../../services/venueService';
import eventService, { EventTypeDto } from '../../services/eventService';
import {
  PlannedRecurringEvent,
  WeeklySchedule,
  PlannedEventQuery,
} from '../../types/plannedEvents';
import { Venue } from '../../types/venue';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useFeedback } from '../../contexts/FeedbackContext';

const WeeklyPlanner: React.FC = () => {
  const confirm = useConfirm();
  const { showSuccess } = useFeedback();

  // State for data
  const [weeklySchedule, setWeeklySchedule] = useState<WeeklySchedule>({
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
  });
  const [venues, setVenues] = useState<Venue[]>([]);
  const [eventTypes, setEventTypes] = useState<EventTypeDto[]>([]);

  // State for filters
  const [selectedVenueId, setSelectedVenueId] = useState<string>('');
  const [selectedEventTypeId, setSelectedEventTypeId] = useState<string>('');

  // State for loading and errors
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [loadingVenues, setLoadingVenues] = useState<boolean>(true);
  const [loadingEventTypes, setLoadingEventTypes] = useState<boolean>(true);

  // State for dialog
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [editingEvent, setEditingEvent] = useState<PlannedRecurringEvent | undefined>(undefined);
  const [preselectedDay, setPreselectedDay] = useState<string | undefined>(undefined);

  // Load venues and event types on mount
  useEffect(() => {
    const loadVenues = async () => {
      try {
        setLoadingVenues(true);
        const venuesData = await venueService.getActiveVenues();
        setVenues(venuesData);
      } catch (error) {
        console.error('Error loading venues:', error);
      } finally {
        setLoadingVenues(false);
      }
    };

    const loadEventTypes = async () => {
      try {
        setLoadingEventTypes(true);
        const eventTypesData = await eventService.getEventTypes();
        setEventTypes(eventTypesData);
      } catch (error) {
        console.error('Error loading event types:', error);
      } finally {
        setLoadingEventTypes(false);
      }
    };

    loadVenues();
    loadEventTypes();
  }, []);

  // Load weekly schedule
  const loadWeeklySchedule = async () => {
    try {
      setLoading(true);
      setError('');

      const query: PlannedEventQuery = {
        isActive: true,
      };

      if (selectedVenueId) {
        query.venueId = selectedVenueId;
      }

      if (selectedEventTypeId) {
        query.eventTypeId = selectedEventTypeId;
      }

      const schedule = await plannedEventService.getWeeklySchedule(query);
      setWeeklySchedule(schedule);
    } catch (error) {
      // [Web UX 3. csomag (D), 2026-10-04] a `plannedEventService` csomagolt hibájának `.message`-e ANGOL előtagot hordoz
      // („loading weekly schedule failed: Request failed with status code 500"); a csomagolás megőrzi a
      // `response`-t, így a közös segéd a szerver magyar indoklását / a státuszkódot adja.
      console.error('Error loading weekly schedule:', error);
      setError(apiErrorMessage(error, 'Hiba történt a heti beosztás betöltése során'));
    } finally {
      setLoading(false);
    }
  };

  // Load schedule when filters change
  useEffect(() => {
    loadWeeklySchedule();
  }, [selectedVenueId, selectedEventTypeId]);

  // Handler for filter changes
  const handleFilterChange = (venueId: string, eventTypeId: string) => {
    setSelectedVenueId(venueId);
    setSelectedEventTypeId(eventTypeId);
  };

  // Handler for clearing filters
  const handleClearFilters = () => {
    setSelectedVenueId('');
    setSelectedEventTypeId('');
  };

  // Handler for creating new event
  const handleCreateEvent = (dayOfWeek?: string) => {
    setEditingEvent(undefined);
    setPreselectedDay(dayOfWeek);
    setDialogOpen(true);
  };

  // Handler for editing event
  const handleEditEvent = (event: PlannedRecurringEvent) => {
    setEditingEvent(event);
    setPreselectedDay(undefined);
    setDialogOpen(true);
  };

  // Handler for deleting event
  const handleDeleteEvent = async (plannedEventId: string) => {
    const confirmed = await confirm({
      title: 'Esemény Törlése',
      message: 'Biztosan törölni szeretnéd ezt az eseményt? Ez a művelet nem vonható vissza.',
      confirmLabel: 'Törlés',
      destructive: true,
    });
    if (!confirmed) return;

    try {
      await plannedEventService.deletePlannedEvent(plannedEventId);
      await loadWeeklySchedule();
      showSuccess('Az esemény törölve.');
    } catch (error) {
      console.error('Error deleting planned event:', error);
      setError(apiErrorMessage(error, 'Hiba történt az esemény törlése során'));
    }
  };

  // Handler for dialog close
  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingEvent(undefined);
    setPreselectedDay(undefined);
  };

  // Handler for dialog save
  const handleDialogSave = async () => {
    await loadWeeklySchedule();
  };

  // Handler for participant click (placeholder)
  const handleParticipantClick = (userId: string) => {
    console.log('Participant clicked:', userId);
    // TODO: Show participant details or navigate to user profile
  };

  // Handler for adding participant via drag-and-drop
  const handleParticipantDrop = async (eventId: string, userId: string) => {
    try {
      await plannedEventService.addParticipant(eventId, userId);
      await loadWeeklySchedule();
      showSuccess('A résztvevő hozzáadva.');
    } catch (error) {
      console.error('Error adding participant:', error);
      setError(apiErrorMessage(error, 'Hiba történt a résztvevő hozzáadása során'));
    }
  };

  // Handler for removing participant
  const handleParticipantRemove = async (eventId: string, userId: string) => {
    try {
      await plannedEventService.removeParticipant(eventId, userId);
      await loadWeeklySchedule();
      showSuccess('A résztvevő eltávolítva.');
    } catch (error) {
      console.error('Error removing participant:', error);
      setError(apiErrorMessage(error, 'Hiba történt a résztvevő eltávolítása során'));
    }
  };

  // Get ordered days for rendering
  const orderedDays = getOrderedDaysOfWeek();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h4"
          sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
        >
          Heti Beosztás
        </Typography>

        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleCreateEvent()}
          sx={{
            alignSelf: { xs: 'flex-start', sm: 'auto' },
          }}
        >
          Új Esemény
        </Button>
      </Box>

      {/* Filter Bar */}
      <Box sx={{ mb: 3 }}>
        <FilterBar
          selectedVenue={selectedVenueId}
          selectedEventType={selectedEventTypeId}
          venues={venues}
          eventTypes={eventTypes}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          loadingVenues={loadingVenues}
          loadingEventTypes={loadingEventTypes}
        />
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        /* Weekly Schedule Grid */
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            pb: 2,
          }}
        >
          {orderedDays.map((day) => {
            const dayKey = day.toLowerCase() as keyof WeeklySchedule;
            const dayEvents = weeklySchedule[dayKey] || [];

            return (
              <Box
                key={day}
                sx={{
                  minWidth: { xs: 280, sm: 300, md: 320 },
                  flex: { xs: '0 0 280px', sm: '0 0 300px', md: '1 1 0' },
                  maxWidth: { md: 'calc((100% - 48px) / 7)' },
                }}
              >
                <DayColumn
                  day={day}
                  events={dayEvents}
                  onEditEvent={handleEditEvent}
                  onDeleteEvent={handleDeleteEvent}
                  onParticipantClick={handleParticipantClick}
                  onParticipantDrop={handleParticipantDrop}
                  onParticipantRemove={handleParticipantRemove}
                />
              </Box>
            );
          })}
        </Box>
      )}

      {/* Event Dialog */}
      <PlannedEventDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleDialogSave}
        event={editingEvent}
        preselectedDay={preselectedDay}
      />
    </Box>
  );
};

export default WeeklyPlanner;
